import { FlashcardDecksService } from "@/client"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { Field } from "@/components/ui/field"
import { RichTextEditor } from "@/components/ui/rich-text-editor"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { containsLocalImageUrls } from "@/utils/richTextImages"
import { Button, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"

export const Route = createFileRoute(
  "/_layout/flashcards/$deckId/cards/$cardId/edit",
)({
  component: EditCardPage,
})

function EditCardPage() {
  const { deckId, cardId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const breadcrumbs = useRouteBreadcrumbs("flashcards.index", [
    { label: "Edit Deck", href: `/flashcards/${deckId}/edit` },
    { label: "Edit Card" },
  ])

  const { data, isLoading } = useQuery({
    queryKey: ["flashcard-deck-cards", deckId],
    queryFn: () => FlashcardDecksService.listCards({ deckId }),
  })
  const card = data?.data.find((item) => item.id === cardId)

  const [front, setFront] = useState("")
  const [back, setBack] = useState("")
  const [frontError, setFrontError] = useState("")
  const [richTextUploadCount, setRichTextUploadCount] = useState(0)
  const isUploadingRichTextImage = richTextUploadCount > 0
  const handleRichTextUploadStateChange = (isUploading: boolean) => {
    setRichTextUploadCount((count) =>
      Math.max(0, count + (isUploading ? 1 : -1)),
    )
  }

  useEffect(() => {
    if (card) {
      setFront(card.front)
      setBack(card.back)
    }
  }, [card])

  const mutation = useMutation({
    mutationFn: (values: { front: string; back: string }) =>
      FlashcardDecksService.updateCard({ deckId, cardId, requestBody: values }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flashcard-deck-cards", deckId],
      })
      navigate({ to: `/flashcards/${deckId}/edit` })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!front.trim()) {
      setFrontError("Front is required")
      return
    }
    if (
      isUploadingRichTextImage ||
      containsLocalImageUrls(front) ||
      containsLocalImageUrls(back)
    ) {
      setFrontError("Wait for images to finish uploading before saving.")
      return
    }
    setFrontError("")
    mutation.mutate({ front, back })
  }

  if (isLoading) {
    return (
      <PageShell title="Edit Card" breadcrumbs={breadcrumbs}>
        <PageSkeleton variant="detail" />
      </PageShell>
    )
  }

  if (!card) {
    return (
      <PageShell title="Edit Card" breadcrumbs={breadcrumbs}>
        <Text color="gray.500">Card not found.</Text>
      </PageShell>
    )
  }

  return (
    <PageShell title="Edit Card" breadcrumbs={breadcrumbs}>
      <form onSubmit={handleSubmit}>
        <VStack gap={4} align="stretch">
          <Field
            label="Front (question / prompt)"
            invalid={!!frontError}
            errorText={frontError}
          >
            <RichTextEditor
              value={front}
              onChange={setFront}
              onUploadStateChange={handleRichTextUploadStateChange}
              onUploadError={setFrontError}
              placeholder="Front of card — paste screenshots or type here"
              minHeight="140px"
            />
          </Field>
          <Field label="Back (answer / rationale)">
            <RichTextEditor
              value={back}
              onChange={setBack}
              onUploadStateChange={handleRichTextUploadStateChange}
              onUploadError={setFrontError}
              placeholder="Back of card — paste screenshots or type here"
              minHeight="200px"
            />
          </Field>
          {mutation.isError && (
            <Text color="red.500" fontSize="sm">
              Failed to save card.
            </Text>
          )}
          <Button
            type="submit"
            disabled={mutation.isPending || isUploadingRichTextImage}
            loading={mutation.isPending || isUploadingRichTextImage}
          >
            Save Card
          </Button>
        </VStack>
      </form>
    </PageShell>
  )
}
